import { ExamineeContactInfo } from "@fadmin/tabs/assessments/store/newExamineeSlice";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ExistingAssessments, Sex, Race, Ethnicity, Paginated } from "./generalTypes";

export interface ExistingExaminee extends ExamineeContactInfo {
    lastAssessment: string;
    age: number;
    birthDate: Date;
    sex: Sex;
    location: string;
    clinicianTitle: string;
    clinicianLastName: string;
    clinicianFirstName: string;
    race: Race;
    ethnicity: Ethnicity;
    primaryLanguage: string;
    secondaryLanguages?: string[];
    highestLevelEducation: string;
}

export interface ExistingExaminees extends Paginated<ExistingExaminee> {}

export type getLocationExamineesSortOptions =
    | "lastName"
    | "firstName"
    | "email"
    | "age"
    | "birthDate"
    | "sex"
    | "location"
    | "clinician"
    | "race";

export interface UpcomingAssessment {
    id: number;
    examineeId: number;
    examineeFirstName: string;
    examineeLastName: string;
    examineeEmail: string;
    examineePhoneNumber: string;
    createdBy: string;
    scheduledFor: string;
}

export interface NewUpcomingAssessmentId {
    id: number;
}

export interface OrgLocation {
    id: number;
    displayId: string;
    name: string;
    phoneNumber: string;
    email: string;
    addressLine1: string;
    addressLine2?: string;
    addressCity: string;
    addressState: string;
    addressZipCode: string;
}

export interface OrgLocationApi {
    id: string;
    name: string;
    phoneNumber: string;
    email: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    zipCode: string;
}

interface LocationsSlice {
    locations: OrgLocation[];
    currentLocation?: OrgLocation;
    existingExaminees?: ExistingExaminee[];
    upcomingAssessments?: UpcomingAssessment[];
    completedAssessments?: ExistingAssessments;
    examineesCount?: number;
}

export const initialState: LocationsSlice = {
    locations: []
};

const locationsSlice = createSlice({
    name: "orgLocations",
    initialState,
    reducers: {
        updateLocations(state, action: PayloadAction<OrgLocation[]>) {
            state.locations = action.payload;
            state.existingExaminees = [];
            state.upcomingAssessments = [];
            state.completedAssessments = undefined;
            state.examineesCount = undefined;
        },
        updateCurrentLocation(state, action: PayloadAction<OrgLocation | undefined>) {
            state.currentLocation = action.payload;
        },
        updateLocationExaminees(state, action: PayloadAction<ExistingExaminee[]>) {
            state.existingExaminees = action.payload;
        },
        updateUpcomingAssessments(state, action: PayloadAction<UpcomingAssessment[]>) {
            state.upcomingAssessments = action.payload;
        },
        updateCompletedAssessments(state, action: PayloadAction<ExistingAssessments>) {
            state.completedAssessments = action.payload;
        },
        updateExamineesCount(state, action: PayloadAction<{ examineesCount: number }>) {
            state.examineesCount = action.payload.examineesCount;
        }
    }
});

export const parseOrgLocationToApi = (loc: OrgLocation): OrgLocationApi => ({
    id: loc.displayId,
    name: loc.name,
    phoneNumber: loc.phoneNumber,
    email: loc.email,
    addressLine1: loc.addressLine1,
    addressLine2: loc.addressLine2,
    city: loc.addressCity,
    state: loc.addressState,
    zipCode: loc.addressZipCode
});

export const {
    updateLocations,
    updateCurrentLocation,
    updateLocationExaminees,
    updateUpcomingAssessments,
    updateCompletedAssessments,
    updateExamineesCount
} = locationsSlice.actions;

export default locationsSlice.reducer;
